import _ from 'lodash';
import PropTypes from 'prop-types';
import moment from 'moment';
import React, { useState, useRef } from 'react';
import {
  Button,
  Icon,
} from 'semantic-ui-react';

import { captureException } from '@playbookux/playbook-web-core';
import {
  exportSegmentData,
  downloadExportProject,
} from '../../../../../core/services/export';
import ExportDialog from '../../../../../core/components/export-project-dialog';
import {
  Button as ButtonLink,
  Column,
  Container,
  ContainerGenerateLink,
  CreateError,
  CreateSuccess,
  Data,
  ExportButton,
  Grid,
  Header,
  HeaderRow,
  Input,
  NoDataMessage,
  Row,
  SegmentName,
  UserInterviewsLink,
} from './styles';

import Modal from '../../../../../core/components/multiple-inputs-modal';
import CapResponsesModal from '../../cap-responses-modal';
import { config } from '../../../../../config';
import { createLink, fetchRecruitingLinks } from './graphql';

const RecruitingLink = (props) => {
  const [isCreating, setIsCreating] = useState(false);
  const [showMessage, setShowMessage] = useState(false);
  const [success, setSuccess] = useState(false);
  const [value, setValue] = useState('');
  const [capResponses, setCapResponses] = useState(1);
  const [demographicsFormRequired, setDemographicsFormRequired] = useState(true);
  const [isDisqualifyQuestionOptionsRequired, setIsDisqualifyQuestionOptionsRequired] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showExport, setShowExport] = useState(false);
  const [loadingExport, setLoadingExport] = useState(false);
  const [errorExport, setErrorExport] = useState(false);
  const [exportBlob, setExportBlob] = useState({});
  const copyLinkRef = useRef();

  const {
    project,
    segment,
    refetch,
  } = props;

  const handleCapResponsesChange = (event, { value: tempValue }) => {
    setCapResponses(tempValue);
  };

  const handleExport = async () => {
    try {
      let blobInfo = {};
      setShowExport(true);
      setLoadingExport(true);
      blobInfo = await exportSegmentData(segment.id, true, `segment_${segment.id}`);

      setExportBlob(blobInfo);
      setLoadingExport(false);
    } catch (err) {
      setLoadingExport(false);
      setErrorExport(true);
      captureException('HANDLE_EXPORT_FAILED', err);
    }
  };

  const handleDownloadExport = async () => {
    try {
      await downloadExportProject(exportBlob);
      setShowExport(false);
    } catch (err) {
      captureException('HANDLE_EXPORT_FAILED', err);
    }
  };

  const handleCloseExport = () => {
    setShowExport(false);
    setErrorExport(false);
  };

  const handleToggleModal = () => {
    setIsCreating(!isCreating);
  };

  const handleCopy2Clipboard = (e) => {
    copyLinkRef.current.select();
    document.execCommand('copy');
    e.target.focus();
    setCopied(true);
  };

  const handleCreateLink = () => {
    setIsCreating(true);
    setShowMessage(false);

    createLink(project.id, segment.id, Number(capResponses), demographicsFormRequired, isDisqualifyQuestionOptionsRequired)
      .then((resp) => {
        const newState = {
          isCreating: false,
          showMessage: true,
        };
        if (resp
          && resp.data
          && resp.data.createRecruitingLink
          && resp.data.createRecruitingLink.link) {
          newState.success = true;
          newState.value = resp.data.createRecruitingLink.link;
        } else {
          newState.success = false;
        }
        setIsCreating(newState.isCreating);
        setShowMessage(newState.showMessage);
        setSuccess(newState.success);
        if (newState.success) {
          setValue(`${newState.value}`);
        }
        fetchRecruitingLinks(project.id);
        refetch();
      })
      .catch(() => {
        setIsCreating(false);
        setShowMessage(true);
        setSuccess(false);
      }).finally(() => {
        // set to default values
        setIsDisqualifyQuestionOptionsRequired(false);
        setDemographicsFormRequired(true);
      });
  };

  const modalInputs = {
    capResponses: {
      label: 'Cap # Responses:',
      placeholder: 'Enter a number',
      onChange: handleCapResponsesChange,
      value: capResponses,
      inputType: 'text',
    },
    demographicsFormRequired: {
      label: 'Testers will fill out their own demographics, employment and screener surveys',
      placeholder: '',
      onChange: () => setDemographicsFormRequired(!demographicsFormRequired),
      value: demographicsFormRequired,
      inputType: 'checkbox',
    },
    isDisqualifyQuestionOptionsRequired: {
      label: 'Uncheck this box if you want to remove disqualified options from this particular test session link.',
      placeholder: '',
      onChange: () => setIsDisqualifyQuestionOptionsRequired(!isDisqualifyQuestionOptionsRequired),
      value: isDisqualifyQuestionOptionsRequired,
      inputType: 'checkbox',
    },
  };

  const finalModalInputs = () => {
    // strip away any inputs that are not needed
    const finalInputs = { ...modalInputs };
    const isFromPlaybookParticipants = project.participantsSource === 'playbook';

    if (!isFromPlaybookParticipants) {
      delete finalInputs.isDisqualifyQuestionOptionsRequired;
      delete finalInputs.demographicsFormRequired;
      if (demographicsFormRequired) {
        setDemographicsFormRequired(false);
      }
    }

    return finalInputs;
  };

  return (
    <Container>
      <Modal
        open={isCreating}
        onClose={handleToggleModal}
        inputs={finalModalInputs()}
        buttonText="Generate"
        onSubmit={handleCreateLink}
        icon="checkmark"
      />
      <ExportDialog
        show={showExport}
        loadingExport={loadingExport}
        downloadExport={handleDownloadExport}
        handleClose={handleCloseExport}
        errorExport={errorExport}
      />

      <>
        {project.participantsSource === 'playbook' && <SegmentName>{segment.name}</SegmentName>}
        <ContainerGenerateLink>
          <ButtonLink onClick={handleToggleModal}>Generate Recruiting URL</ButtonLink>
          <Input
            ref={copyLinkRef}
            placeholder="Click the button to generate link"
            value={value}
          />
          <Button onClick={handleCopy2Clipboard}>
            <Icon name="copy" />
          </Button>

          {showMessage && success && copied
            && <CreateSuccess>Link copied to clipboard!</CreateSuccess>}

          {showMessage && success && !copied
            && <CreateSuccess>Link generated!</CreateSuccess>}

          {showMessage && !success
            && <CreateError>Error while creating the link</CreateError>}
        </ContainerGenerateLink>

        {['moderated', 'unmoderated'].includes(project.testType) && (
          <ExportButton onClick={handleExport}>Export segment</ExportButton>
        )}

        <Grid>
          <HeaderRow>
            <Column verticalAlign="middle" width={7}>
              <Header>Link</Header>
            </Column>

            <Column verticalAlign="middle" width={3}>
              <Header>Max tests</Header>
            </Column>

            <Column verticalAlign="middle" width={4}>
              <Header>Created On</Header>
            </Column>
          </HeaderRow>

          {_.isEmpty(segment.recruitParticipantTokens)
            && <NoDataMessage>No link found</NoDataMessage>}

          {!_.isEmpty(segment.recruitParticipantTokens)
            && _.filter(segment.recruitParticipantTokens, { deletedAt: null }).map((link) => (
              <Row key={link}>
                <Column width={7}>
                  <Data>
                    {`${config.REACT_APP_TESTER_PLATFORM_URI}/guest-tester?token=${link.token}`}
                  </Data>
                </Column>

                <Column width={3}>
                  <Data>
                    {link.maximumTests}

                    <CapResponsesModal
                      number={link.maximumTests}
                      type="testers"
                      label="Response Cap"
                      projectId={project.id}
                      segmentId={segment.id}
                      linkId={link.id}
                      refetch={refetch}
                    />
                  </Data>
                </Column>

                <Column width={4}>
                  <Data>
                    {moment(link.createdAt).format('MM/DD/YYYY HH:mm')}
                  </Data>
                </Column>

                {
                  link.isUserInterviews && (
                    <Column width={2}>
                      <UserInterviewsLink>
                        User Interviews Link
                      </UserInterviewsLink>
                    </Column>
                  )
                }
              </Row>
            ))}
        </Grid>
      </>
    </Container>
  );
};

RecruitingLink.propTypes = {
  project: PropTypes.isRequired,
  segment: PropTypes.isRequired,
  refetch: PropTypes.func.isRequired,
};

export default RecruitingLink;
